import { DocumentSnapshot, Timestamp, logger } from "../firestoreImports"
import { z } from "zod"
import type { User } from "./user"

const abuseSwapSchema = z.object({
    abuserId: z.string().min(1),
    abuserReservationId: z.string().min(1),
    newSpotId: z.string().min(1),
    newSpot: z.string().min(1)
})

const abuseInfoSchema = z.object({
    reservationId: z.string().min(1),
    abuserLicense: z.string().nullish(),
    spotId: z.string().min(1),
    abuseeId: z.string().min(1),
    newReservationId: z.string().nullish(),
    swap: abuseSwapSchema.nullish(),
    timestampUpdated: z.custom<Timestamp>().nullish(),
    timestampCreated: z.custom<Timestamp>().nullish(),
    pictureFilename: z.string().nullish()
})

export interface AbuseSwapInfo {
    abuserId: string
    abuserReservationId: string
    newSpotId: string
    newSpot: string
}

export interface IAbuseInfo {
    id: string
    reservationId: string
    abuserLicense?: string
    spotId: string
    abuseeId: string
    newReservationId?: string
    swap?: AbuseSwapInfo
    /** @deprecated Deprecated in 2.0 **/
    timestampUpdated?: Date
    timestampCreated?: Date
    pictureFilename?: string
}

export interface AbuseInfo extends IAbuseInfo {}
export class AbuseInfo {
    // Helper (not persisted)
    abuseeData?: User

    constructor(params: IAbuseInfo) {
        Object.assign(this, params)
    }

    static fromData(doc: DocumentSnapshot): AbuseInfo | null {
        const raw = doc.data()
        if (!raw) {
            logger.warn(`Cannot create AbuseInfo ${doc.id} from snapshot: missing data`)
            return null
        }

        try {
            const parsed = abuseInfoSchema.parse(raw)
            return new AbuseInfo({
                id: doc.id,
                reservationId: parsed.reservationId,
                abuserLicense: parsed.abuserLicense ?? undefined,
                spotId: parsed.spotId,
                abuseeId: parsed.abuseeId,
                newReservationId: parsed.newReservationId ?? undefined,
                swap: parsed.swap ?? undefined,
                timestampUpdated: parsed.timestampUpdated?.toDate() ?? undefined,
                timestampCreated: parsed.timestampCreated?.toDate() ?? undefined,
                pictureFilename: parsed.pictureFilename ?? undefined
            })
        } catch (error) {
            logger.error(`Cannot create AbuseInfo ${doc.id} from snapshot:`, error)
            return null
        }
    }

    asData(): { [k: string]: any } {
        return abuseInfoSchema.parse({
            reservationId: this.reservationId,
            abuserLicense: this.abuserLicense,
            spotId: this.spotId,
            abuseeId: this.abuseeId,
            newReservationId: this.newReservationId,
            swap: this.swap,
            timestampUpdated: this.timestampUpdated ?? Timestamp.now(),
            timestampCreated: this.timestampCreated ?? Timestamp.now(),
            pictureFilename: this.pictureFilename
        })
    }

    static compare(a: AbuseInfo, b: AbuseInfo): number {
        let licenseCompare = 0
        if (a.abuserLicense && b.abuserLicense) {
            licenseCompare = a.abuserLicense.localeCompare(b.abuserLicense)
        }
        if (licenseCompare === 0 && b.timestampCreated && a.timestampCreated) {
            return b.timestampCreated.getTime() - a.timestampCreated.getTime()
        }
        return licenseCompare
    }
}
